import { DetectionZoneChangeEventTrigger, DetectionZoneDwellEventTrigger, DetectionZoneEntryEventTrigger, DetectionZoneExitEventTrigger, DetectionZonePresenceEventTrigger, EventTriggerType, ImpendingZoneEntryEventTrigger, LaneLineEventTrigger, NearMissEventTrigger, ScreenLineChangeEventTrigger, ScreenLineInboundEventTrigger, ScreenLineOutboundEventTrigger, SpeedingEventTrigger, TargetDetectedEventTrigger, WrongWayEventTrigger } from "@app/shared";
import { InputRow, Select } from "@app/dashboard/src/components/shared/inputs";
import { EditEventTrigger } from "./EditEventTrigger";
import { isCloudProcessingSupported } from "./cloudProcessing";

export type EventTriggerTypes = DetectionZoneChangeEventTrigger | DetectionZoneDwellEventTrigger | DetectionZoneEntryEventTrigger | DetectionZoneExitEventTrigger | DetectionZonePresenceEventTrigger | ImpendingZoneEntryEventTrigger | SpeedingEventTrigger | NearMissEventTrigger | ScreenLineChangeEventTrigger | ScreenLineInboundEventTrigger | ScreenLineOutboundEventTrigger | TargetDetectedEventTrigger | WrongWayEventTrigger | LaneLineEventTrigger;

// The device a web config page is running on. In offline mode the editor can't look devices up (the
// unit's local API serves only itself), so the page hands over the one device every trigger runs on.
export type OfflineTriggerDevice = { id?: string; name?: string | null; takeTargetPhotos?: boolean };

export type EventTriggerEditorProps = {
    trigger: EventTriggerTypes | undefined;
    facilityId?: string;
    onChange: (trigger: EventTriggerTypes | undefined) => any;
    offlineMode?: boolean;
    offlineDevice?: OfflineTriggerDevice;
};

const getEditor = (props: EventTriggerEditorProps) => {
    switch (props.trigger?.triggerType) {
        case EventTriggerType.DetectionZoneChange:
            return (
                <>
                    <EditEventTrigger model={DetectionZoneChangeEventTrigger} hasDetectionZone={true} {...props} />
                </>
            );
        case EventTriggerType.DetectionZoneDwell:
            return (
                <>
                    <EditEventTrigger model={DetectionZoneDwellEventTrigger} hasDetectionZone={true} {...props} />
                </>
            );
        case EventTriggerType.DetectionZoneEntry:
            return (
                <>
                    <EditEventTrigger model={DetectionZoneEntryEventTrigger} hasDetectionZone={true} {...props} />
                </>
            );
        case EventTriggerType.DetectionZoneExit:
            return (
                <>
                    <EditEventTrigger model={DetectionZoneExitEventTrigger} hasDetectionZone={true} {...props} />
                </>
            );
        case EventTriggerType.DetectionZonePresence:
            return (
                <>
                    <EditEventTrigger model={DetectionZonePresenceEventTrigger} hasDetectionZone={true} {...props} />
                </>
            );
        case EventTriggerType.ImpendingZoneEntry:
            return (
                <>
                    <EditEventTrigger model={ImpendingZoneEntryEventTrigger} hasDetectionZone={true} {...props} />
                </>
            );
        case EventTriggerType.NearMissEvent:
            return (
                <>
                    <EditEventTrigger model={NearMissEventTrigger} {...props} />
                </>
            );
        case EventTriggerType.ScreenLineChange:
            return (
                <>
                    <EditEventTrigger model={ScreenLineChangeEventTrigger} hasScreenline={true} {...props} />
                </>
            );
        case EventTriggerType.ScreenLineInbound:
            return (
                <>
                    <EditEventTrigger model={ScreenLineInboundEventTrigger} hasScreenline={true} {...props} />
                </>
            );
        case EventTriggerType.ScreenLineOutbound:
            return (
                <>
                    <EditEventTrigger model={ScreenLineOutboundEventTrigger} hasScreenline={true} {...props} />
                </>
            );
        case EventTriggerType.SpeedingEvent:
            return (
                <>
                    <EditEventTrigger model={SpeedingEventTrigger} hasScreenline={true} hasDetectionZone={true} {...props} />
                </>
            );
        case EventTriggerType.TargetDetected:
            return (
                <>
                    <EditEventTrigger model={TargetDetectedEventTrigger} hasDetectionZone={true} {...props} />
                </>
            );
        case EventTriggerType.WrongWayEvent:
            return (
                <>
                    <EditEventTrigger model={WrongWayEventTrigger} hasDetectionZone {...props} />
                </>
            );
        case EventTriggerType.LaneLineEvent:
            return (
                <>
                    <EditEventTrigger model={LaneLineEventTrigger} hasLaneLine={true} {...props} />
                </>
            );
    }
};

export const EventTriggerEditor = (props: EventTriggerEditorProps) => {
    if (!props || !props.trigger) return <>No trigger to edit. Please try again</>;

    return (
        <>
            <InputRow>
                <Select
                    label="Trigger Type"
                    emptyOptionText="Select Event Trigger Type"
                    emptyOptionDisabled={true}
                    options={[
                        { label: "Detection Zone Change", value: EventTriggerType.DetectionZoneChange },
                        { label: "Detection Zone Dwell", value: EventTriggerType.DetectionZoneDwell },
                        { label: "Detection Zone Entry", value: EventTriggerType.DetectionZoneEntry },
                        { label: "Detection Zone Exit", value: EventTriggerType.DetectionZoneExit },
                        { label: "Detection Zone Presence", value: EventTriggerType.DetectionZonePresence },
                        { label: "Impending Zone Entry", value: EventTriggerType.ImpendingZoneEntry },
                        { label: "Near Miss", value: EventTriggerType.NearMissEvent },
                        { label: "Screen Line Change", value: EventTriggerType.ScreenLineChange },
                        { label: "Screen Line Inbound", value: EventTriggerType.ScreenLineInbound },
                        { label: "Screen Line Outbound", value: EventTriggerType.ScreenLineOutbound },
                        { label: "Speeding", value: EventTriggerType.SpeedingEvent },
                        { label: "Target Detected", value: EventTriggerType.TargetDetected },
                        { label: "Wrong Way", value: EventTriggerType.WrongWayEvent },
                        { label: "Lane Line", value: EventTriggerType.LaneLineEvent },
                    ]}
                    disabled={!!props?.trigger?.id}
                    value={props?.trigger?.triggerType ?? ""}
                    onChange={(v) => {
                        if (!props?.trigger) return props?.onChange(undefined);
                        const triggerType = parseInt(v.target?.value) as EventTriggerType;
                        // Trigger types the server can't process in the cloud must stay on-device — and so
                        // must any trigger carrying a device-delivered endpoint. Without the second clause
                        // this recompute would clear the pin EditEventTrigger just applied whenever the new
                        // trigger type happens to be cloud-capable.
                        const hasDeviceOnlyEndpoint = (props.trigger.eventTriggerClientEndpoints ?? [])
                            .some((etc) => etc.clientEndpoint?.deliversFromDevice === true);
                        const processOnDevice = isCloudProcessingSupported(triggerType) && !hasDeviceOnlyEndpoint
                            ? props.trigger.processOnDevice
                            : true;
                        props.onChange({ ...props.trigger, triggerType, processOnDevice });
                    }}
                />
            </InputRow>

            {getEditor(props)}
        </>
    );
};
